import { Segment } from '@dna/analytics'
import { WithProfiler } from '@dna/mixins'
import { chevron } from '@dna/paths'
import styles from '@dna/styles'
import { t } from '@dna/utils'
import { LitElement, createRef, css, html, nothing, ref, repeat } from '@lit'

class DNAGridSort extends WithProfiler(LitElement) {
  static properties = {
    sort: {
      type: Object,
    },
    hidePriceControlOptions: {
      type: Boolean,
    },
    showListBox: {
      type: Boolean,
      state: true,
    },
  }

  constructor() {
    super()
    this.sort = {
      options: [],
      selected: '',
    }
    this.hidePriceControlOptions = false
    this.showListBox = false
    this.$combobox = createRef()
    this.$optionsList = createRef()
    this.$firstOption = createRef()
    this.$lastOption = createRef()
  }

  connectedCallback() {
    super.connectedCallback()
    this.router = __DNA_GLOBAL__.utils.queryRouter
  }

  disconnectedCallback() {
    super.disconnectedCallback()
  }

  _handleShowListBox() {
    this.showListBox = !this.showListBox
    if (this.showListBox) {
      this.updateComplete.then(() => this.$firstOption?.value?.focus())
      this._trackSortsViewed()
    } else {
      this.$combobox?.value?.focus()
    }
  }

  _sortChange(event) {
    const selectedValue = event.target.getAttribute('value')?.split('|')
    const sortValue = this.sort?.options?.find((option) => {
      return (
        option.sort_by === selectedValue[0] &&
        option.sort_order === selectedValue[1]
      )
    })

    if (sortValue) {
      this.router.query.update({
        page: 1,
        sort: sortValue.sort_by,
        sort_order: sortValue.sort_order,
      })
      this._handleShowListBox()
      this._trackSortSelected(sortValue)
    }
  }

  _handleSortKeydown(event, index) {
    event.preventDefault()
    if (event.key === 'Enter' || event.key === ' ') {
      this._sortChange(event)
    } else if (event.key === 'Escape') {
      this._handleShowListBox()
    } else if (
      event.key === 'ArrowUp' ||
      (event.key === 'Tab' && event.shiftKey)
    ) {
      if (this.shadowRoot.activeElement === this.$firstOption?.value) {
        this.$lastOption?.value?.focus()
      } else {
        this.$optionsList?.value?.children[index - 1]?.focus()
      }
    } else if (event.key === 'Tab' || event.key === 'ArrowDown') {
      if (
        this.shadowRoot.activeElement ===
        this.$optionsList?.value?.children[
          this.$optionsList.value.children.length - 1
        ]
      ) {
        this.$firstOption?.value?.focus()
      } else {
        this.$optionsList?.value?.children[index + 1]?.focus()
      }
    }
  }

  _trackSortsViewed() {
    // @segment
    Segment.protect(() => {
      Segment.track(Segment.CONSTANTS.EVENTS.SORTS_VIEWED, {
        sorts: this.sort.options
          .map((sort) => sort.display_name)
          ?.toString()
          ?.replaceAll(',', ', '),
      })
    })
  }

  _trackSortSelected(sort) {
    // @segment
    Segment.protect(() => {
      Segment.track(Segment.CONSTANTS.EVENTS.SORT_ADDED, {
        sort_name: sort.display_name,
      })
    })
  }

  render() {
    return html`
      <div class="sorting-options p2">
        <div class="sorting-options__select flex">
          <span class="select__prefix">
            ${t('sections.collection_template.sort_by_label')}:
          </span>
          <ul
            ${ref(this.$combobox)}
            class="select__label"
            aria-controls="sortList"
            aria-expanded=${this.showListBox}
            aria-labelledby="sortLabel"
            role="combobox"
            tabindex="0"
            value=${this.sort?.selected}
            @click=${this._handleShowListBox}
            @keydown=${(e) => {
              if (
                e.key === 'Enter' ||
                e.key === ' ' ||
                (e.key === 'ArrowDown' && !this.showListBox)
              ) {
                e.preventDefault()
                this._handleShowListBox()
              }
            }}
          >
            <li id="sortLabel">
              ${repeat(
                this.sort?.options,
                (option) => `${option.sort_by}|${option.sort_order}`,
                (option) =>
                  `${option.sort_by}|${option.sort_order}` ===
                  this.sort.selected
                    ? option.display_name
                    : nothing,
              )}
            </li>
          </ul>
          <button
            class="select__expand-icon${this.showListBox ? ' active' : ''}"
            tabindex="-1"
            @click=${this._handleShowListBox}
          >${chevron}</button>
        </div>
        <div
          ${ref(this.$optionsList)}
          class="sorting-options__list${this.showListBox ? ' active' : ''}"
          id="sortList"
          aria-label="Select a sorting option"
          role="listbox"
        >
          ${repeat(
            this.sort?.options,
            (option) => `${option.sort_by}|${option.sort_order}`,
            (option, index) => {
              const shouldHidePriceControlOptions =
                this.hidePriceControlOptions &&
                (option.display_name === 'Price (High to Low)' ||
                  option.display_name === 'Price (Low to High)')
              if (!shouldHidePriceControlOptions) {
                return html`
                  <div
                    ${
                      index === 0
                        ? ref(this.$firstOption)
                        : index === this.sort.options.length - 1
                          ? ref(this.$lastOption)
                          : nothing
                    }
                    class="sorting-options__option${`${option.sort_by}|${option.sort_order}` === this.sort.selected ? ' sorting-options__option--selected' : ''}"
                    aria-selected=${`${option.sort_by}|${option.sort_order}` === this.sort.selected}
                    role="option"
                    tabindex="0"
                    value=${option.sort_by}|${option.sort_order}
                    @click=${this._sortChange}
                    @keydown=${(e) => {
                      this._handleSortKeydown(e, index)
                    }}
                  >${option.display_name}</div>
                `
              }
            },
          )}
        </div>
      </div>
    `
  }

  static styles = [
    styles.a11y,
    styles.flex,
    styles.typography,
    css`
      * {
        box-sizing: border-box;
      }

      .sorting-options {
        color: var( --dna-color-midnight);
        font-size: 12px;
        font-weight: 400;
        line-height: 18px;
        letter-spacing: 0.5px;
        margin: var(--dna-space-2) 0;
        position: relative;
      }

      .sorting-options__select {
        background-color: transparent;
        color: var(--dna-color-midnight);
      }

      .sorting-options__select .select__prefix {
        margin-right: var(--dna-space-2);
      }

      .sorting-options__select .select__label {
        cursor: pointer;
        list-style: none;
        margin: 0;
        padding: 0 calc(var(--dna-space-5) / 2) 0 0;
      }

      .select__expand-icon {
        background-color: transparent;
        border: none;
        cursor: pointer;
        display: grid;
        margin: 0;
        padding: 0;
        place-items: center;
      }

      .select__expand-icon svg {
        width: var(--dna-space-4);
        height: var(--dna-space-4);
        transform: rotate(90deg);
        transition: transform 0.2s linear;
      }

      .select__expand-icon.active svg {
        transform: rotate(-90deg);
      }

      .sorting-options__list {
        background-color: var( --dna-brand-reverse);
        border: 1px solid var(--dna-color-light-grey);
        border-radius: 0;
        display: none;
        flex-direction: column;
        gap: var(--dna-space-4);
        min-width: 168px;
        padding: var(--dna-space-4);
        position: absolute;
        top: calc(1em + var(--dna-space-3));
        right: 0;
      }

      .sorting-options__list.active {
        display: flex;
      }

      .sorting-options__option {
        cursor: pointer;
      }

      .sorting-options__option--selected {
        font-weight: 600;
      }

      /* breakpoint-mobile */
      @media screen and (min-width: 768px) {
        .sorting-options {
          font-size: 14px;
          line-height: 20px;
        }

        .sorting-options__list {
          min-width: 220px;
          padding: var(--dna-space-6);
        }
      }
    `,
  ]
}

customElements.define('dna-grid-sort', DNAGridSort)
